import { useCallback, useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

import AuctionNavigation from "../auctionNavigation";
import Carousel from "../carousel";
import ConnectWallet from "../connectWallet";
import WalletSelector from "../walletSelector";

import { AUCTION_CLAIM_STATES, SORT_DIRECTION } from "../../models";

import auctionService from "../../services/auction";
import { FiltersContext } from "../../services/context/filters";
import { W3Context } from "../../services/context";
import getContracts from "../../services/contract/getContracts";

import hammer from "../../assets/hammer.png";
import hammerBlack from "../../assets/hammer_black.png";
import nftIconBlack from "../../assets/nft_icon_black.png";

import "./myAuctionsStyles.css";

const MyAuctions = ({ onClickConnectWallet }) => {
  const { nftsContract } = getContracts();

  const { filters } = useContext(FiltersContext);
  const { active, account, library } = useContext(W3Context);
  const [displayModal, setDisplayModal] = useState(false);
  const [myAuctions, setMyAuctions] = useState([]);
  const [myNFTs, setMyNFTs] = useState([]);

  const parseNFT = useCallback(async (URL, id) => {
    try {
      const response = await fetch(URL);
      const NFTJson = await response.json();

      const { name, image, animation_url, attributes } = NFTJson;
      return {
        tokenId: id,
        name,
        img: image,
        video: animation_url,
        latitude: attributes.shift().value,
        longitude: attributes.shift().value,
        country: attributes.shift().value,
        tier: attributes.shift().value,
        category: attributes.shift().value,
      };
    } catch (err) {
      console.log(err);
    }
  }, []);

  const getURI = useCallback(async () => {
    try {
      if (active) {
        const contract = new library.eth.Contract(
          nftsContract.abi,
          nftsContract.address
        );
        const myAddress = library.utils.toChecksumAddress(account);
        // const myAddress = library.utils.toChecksumAddress(
        //   "0x6D1e3f01892E888353Bc231693469042191aaa83"
        // );

        try {
          const ids = await contract.methods.getWalletNFTs(myAddress).call();
          const promises = ids.reduce(
            (NFTs, id) => [
              ...NFTs,
              parseNFT(
                `https://static.geopoly.io/nfts/${id.toString()}.json`,
                id
              ),
            ],
            []
          );
          const data = await Promise.all(promises);
          const nfts = data.map((nft) => {
            return {
              tokenId: nft.tokenId,
              category: nft.category,
              countryCode: nft.country,
              assetUrl: nft.video,
              imageUrl: nft.img,
              point: [nft.longitude, nft.latitude],
              name: nft.name,
              tier: nft.tier,
              owner: account,
            };
          });

          const filterNfts =
            Object.keys(filters).length > 0
              ? nfts.filter((nft) => {
                  if (filters.name && nft.name) {
                    return nft.name
                      .toLowerCase()
                      .startsWith(filters.name.toLowerCase());
                  }

                  if (filters.tier && filters.category) {
                    return (
                      filters.tier === nft.tier &&
                      filters.category.includes(nft.category)
                    );
                  }

                  if (filters.category) {
                    return filters.category.includes(nft.category);
                  }

                  if (filters.tier) {
                    return filters.tier === nft.tier;
                  }

                  return true;
                })
              : nfts;
          setMyNFTs(filterNfts);
        } catch (err) {
          console.log(err);
        }
      }
    } catch (err) {
      console.log(err);
      setMyNFTs([]);
    }
  }, [
    account,
    active,
    filters,
    library,
    nftsContract.abi,
    nftsContract.address,
    parseNFT,
  ]);

  const getAuctions = useCallback(async () => {
    if (account) {
      const myActiveAndClaimableAuctions = await auctionService.getAll({
        owner: account,
        auctioneerState: [
          AUCTION_CLAIM_STATES.LOCKED,
          AUCTION_CLAIM_STATES.CLAIMABLE,
        ],
        ...filters,
        sort: "expirationDate," + SORT_DIRECTION.ASC,
      });

      setMyAuctions(myActiveAndClaimableAuctions);
    }
  }, [account, filters]);

  useEffect(() => {
    getURI();
    getAuctions();
  }, [getAuctions, getURI]);

  return (
    <Container className="my-auctions">
      <Row className="title mb-2">
        <p>
          <img src={hammer} alt="auctions" />
          <span className="purple">My</span>
          Auctions
        </p>
      </Row>
      <AuctionNavigation
        onClickConnectWallet={onClickConnectWallet}
        showPriceFilter={false}
        showTimeFilter={false}
      />
      <Row className="mt-5">
        <Col>
          {!active ? (
            <>
              <ConnectWallet setDisplayModal={setDisplayModal} />
              <WalletSelector
                displayModal={displayModal}
                setDisplayModal={setDisplayModal}
              />
            </>
          ) : (
            <>
              {myAuctions.length === 0 &&
              myNFTs.length === 0 &&
              Object.keys(filters).length > 0 ? (
                <Container className="no-auctions">
                  <Row className="justify-content-center text-center">
                    <Col>There are no auctions that apply to these filters</Col>
                  </Row>
                </Container>
              ) : (
                <>
                  {myAuctions.length > 0 && (
                    <Container className="px-0">
                      <Row className="justify-content-center">
                        <Col
                          xs={12}
                          className="text-center justify-content-center mx-auto"
                        >
                          <div className="divider-purple align-items-center">
                            <img src={hammerBlack} alt="hammer_1" />
                            <span className="mx-3">Active Auctions</span>
                            <img src={hammerBlack} alt="hammer_2" />
                          </div>
                        </Col>
                        <Col xs={12}>
                          <Carousel NFTs={myAuctions} background={false} />
                        </Col>
                      </Row>
                    </Container>
                  )}
                  {myNFTs.length > 0 && (
                    <Container className="px-0">
                      <Row className="justify-content-center">
                        <Col
                          xs={12}
                          className="text-center justify-content-center mx-auto"
                        >
                          <div className="divider-purple align-items-center">
                            <img src={nftIconBlack} alt="nft_1" />
                            <span className="mx-3">My NFTS</span>
                            <img src={nftIconBlack} alt="nft_2" />
                          </div>
                        </Col>
                        <Col xs={12}>
                          <Carousel NFTs={myNFTs} background={false} />
                        </Col>
                      </Row>
                    </Container>
                  )}
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default MyAuctions;
